import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Banner } from '@/components/Banner'
import { Button } from '@/components/Button'
import { Icon } from '@/components/Icon'
import { Page, PageHeader } from '@/components/Page'
import { Spinner } from '@/components/Spinner'
import { PatientFormFields, usePatientForm } from './PatientForm'

/**
 * Registering a patient, and editing one, as a whole page.
 *
 * In the app itself this form opens as a dialog over whatever you were doing —
 * see `PatientFormModal`. This route stays so that `/patients/new` and
 * `/patients/:id/edit` remain openable directly; both shells share one form.
 */
export function PatientFormScreen({ mode }: { mode: 'create' | 'edit' }) {
  const { patientId = '' } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const state = usePatientForm({
    mode,
    patientId,
    seed: searchParams.get('q') ?? undefined,
    onSaved: (patient) => navigate(`/patients/${patient.id}`, { replace: true }),
  })

  if (mode === 'edit' && state.loading) return <Spinner label="Loading patient" />

  const editing = mode === 'edit'

  return (
    <Page>
      <PageHeader
        eyebrow="Clinic"
        title={editing ? 'Edit patient' : 'Register patient'}
        description={
          editing
            ? 'Identity only. Consultations, prescriptions and documents live on the record.'
            : 'An OP number and a name are enough to start. Everything else can follow.'
        }
      />

      <form className="grid gap-4" onSubmit={state.submit}>
        {state.error && (
          <Banner tone="danger" live>
            {state.error}
          </Banner>
        )}

        <PatientFormFields state={state} autoFocus={mode === 'create'} />

        <div className="flex items-center justify-end gap-4 pt-1">
          <Link
            className="text-sm text-fg-muted"
            to={editing && patientId ? `/patients/${patientId}` : '/patients'}
          >
            Cancel
          </Link>
          <Button type="submit" loading={state.save.isPending}>
            <Icon name="save" />
            {editing ? 'Save changes' : 'Register patient'}
          </Button>
        </div>
      </form>
    </Page>
  )
}
